import { getElevation } from '@m1/geo-utils';
import classNames from 'classnames/bind';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styles from './common.module.scss';

import actions from '@/actions';
import FieldRow from '@/components/ui/FieldRow';
import { FieldNumber } from '@/components/ui/kit/Field';
import { roundToDecimal } from '@/utils/Formatter';

const cx = classNames.bind(styles);

const Command = ({ index, data: command }) => {
  const dispatch = useDispatch();
  const commands = useSelector((state) => state.mission.json.commands);
  const domRefs = useRef({
    latitude: useRef(),
    longitude: useRef(),
    elevation: useRef(),
    relAltitude: useRef(),
    aglAltitude: useRef(),
    mslAltitude: useRef(),
  }).current;

  // 홈 지면 고도
  const homeElevation = useMemo(() => {
    if (commands.length > 0) {
      return commands[0].data.elevation;
    }
    return 0;
  }, [commands]);

  const { mslAltitude, aglAltitude } = useMemo(() => {
    const mslAltitude = command.data.altitude + homeElevation;
    const aglAltitude = mslAltitude - command.data.elevation;

    return { mslAltitude, aglAltitude };
  }, [homeElevation, command.data]);

  useEffect(() => {
    domRefs.latitude.current.value = command.data.position.lat.toFixed(7);
    domRefs.longitude.current.value = command.data.position.lng.toFixed(7);
    domRefs.elevation.current.value = command.data.elevation;
    domRefs.relAltitude.current.value = command.data.altitude.toFixed(1);
    domRefs.aglAltitude.current.value = aglAltitude.toFixed(1);
    domRefs.mslAltitude.current.value = mslAltitude.toFixed(1);
  }, [command.data]);

  const updatePosition = (position) => {
    getElevation(position).then((elevation) => {
      const waypoint = { position, elevation };
      dispatch(actions.mission.moveWaypoint(command.id, waypoint));
    });
  };

  const handleBlur = (e) => {
    const name = e.target.name;
    const value = Number(e.target.value);

    // Latitude 변경된 경우
    if (name === 'latitude') {
      const nextValue = roundToDecimal(value, 7);
      updatePosition({ lat: nextValue, lng: command.data.position.lng });
    }
    // Longitude 변경된 경우
    else if (name === 'longitude') {
      const nextValue = roundToDecimal(value, 7);
      updatePosition({ lat: command.data.position.lat, lng: nextValue });
    }
    // Relative Altitude 변경된 경우
    else if (name === 'relAltitude') {
      const nextValue = roundToDecimal(value);
      dispatch(actions.mission.editWaypoint(index, 'altitude', nextValue));
    }
    // AGL Altitude 변경된 경우
    else if (name === 'aglAltitude') {
      const nextValue = roundToDecimal(command.data.elevation + value - homeElevation);
      dispatch(actions.mission.editWaypoint(index, 'altitude', nextValue));
    }
    // MSL Altitude 변경된 경우
    else if (name === 'mslAltitude') {
      const nextValue = roundToDecimal(value - homeElevation);
      dispatch(actions.mission.editWaypoint(index, 'altitude', nextValue));
    }
  };

  return (
    <div className={cx('container')}>
      <FieldRow label="Latitude">
        <FieldNumber ref={domRefs.latitude} name="latitude" min={-85} max={85} onBlur={handleBlur} />
      </FieldRow>
      <FieldRow label="Longitude">
        <FieldNumber ref={domRefs.longitude} name="longitude" min={-180} max={180} onBlur={handleBlur} />
      </FieldRow>
      <FieldRow label="Elevation" unit="m">
        <FieldNumber ref={domRefs.elevation} name="elevation" readOnly />
      </FieldRow>
      <FieldRow label="Relative Altitude" unit="m">
        <FieldNumber ref={domRefs.relAltitude} name="relAltitude" onBlur={handleBlur} />
      </FieldRow>
      <FieldRow label="AGL Altitude" unit="m">
        <FieldNumber
          ref={domRefs.aglAltitude}
          name="aglAltitude"
          className={cx({ danger: aglAltitude <= 0 })}
          onBlur={handleBlur}
        />
      </FieldRow>
      <FieldRow label="MSL Altitude" unit="m">
        <FieldNumber ref={domRefs.mslAltitude} name="mslAltitude" onBlur={handleBlur} />
      </FieldRow>
    </div>
  );
};

export default Command;
